import { useMemo, memo, useRef } from 'react';
import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

type ColorInputProps = {
  value: string;
  onChange: (color: string) => void;
  width?: string | number;
  sx?: SxProps<Theme>;
  disabled?: boolean;
};

// Accept 3- or 6-digit hex colors (e.g. #fff or #ffffff).
export const isValidHexColor = (color: string): boolean => {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color);
};

const expandShortHex = (shortHex: string) => {
  const r = shortHex.charAt(1);
  const g = shortHex.charAt(2);
  const b = shortHex.charAt(3);
  return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
};

const normalizeHex = (color: string) => {
  if (!color) return color;
  const c = color.trim();
  if (/^#([0-9A-Fa-f]{3})$/.test(c)) return expandShortHex(c);
  if (/^#([0-9A-Fa-f]{6})$/.test(c)) return c.toLowerCase();
  return c;
};

export const ColorInput = memo(
  ({ value, onChange, width = 40, sx = {}, disabled = false }: ColorInputProps) => {
    const colorInputRef = useRef<HTMLInputElement | null>(null);

    const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      // native color input returns a full 6-digit hex
      onChange(normalizeHex(val));
    };

    const pickerValue = useMemo(() => {
      if (isValidHexColor(value)) {
        return normalizeHex(value);
      }
      return '#1976d2';
    }, [value]);

    return (
      <Box
        sx={{
          width,
          minWidth: width,
          display: 'flex',
          flexGrow: 0,
          ...sx,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 24,
            backgroundColor: isValidHexColor(value) ? value : '#999999',
            borderRadius: 1,
            border: '1px solid #ccc',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: disabled ? '#ccc' : '#999',
              boxShadow: disabled ? 'none' : '0 0 8px rgba(0,0,0,0.1)',
            },
          }}
          title={value}
        >
          <input
            ref={colorInputRef}
            type="color"
            value={pickerValue}
            onChange={handlePickerChange}
            disabled={disabled}
            aria-label="Choose color"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              border: 'none',
              padding: 0,
              margin: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          />
        </Box>
      </Box>
    );
  },
);
